import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import { CODOutForDeliveryReportResponse } from 'src/app/core/models/reports/reports.model';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CODOutForDeliveryFormGroup,
  CONSTS,
  FILE_NAME,
  PDF_REPORT,
} from './out-for-delivery.constants';

@Component({
  selector: 'app-out-for-delivery',
  templateUrl: './out-for-delivery.component.html',
  styleUrls: ['./out-for-delivery.component.scss'],
  standalone: false,
})
export class OutForDeliveryComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: CODOutForDeliveryFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef: MatDialogRef<OutForDeliveryComponent> = inject(
    MatDialogRef<OutForDeliveryComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private codsReportsService: CodsReportsService = inject(CodsReportsService);
  private commonUtils: CommonUtils = inject(CommonUtils);
  private reportUtils: ReportsUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  outForDeliveryForm!: FormGroup;
  codOutForDeliveryReport!: CODOutForDeliveryReportResponse;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.outForDeliveryForm = this.fb.group({
      date: [this.currDate],
      reportFormat: [Options.PDF],
    });
  }

  clickExit() {
    this.commonUtils.openExitModal(this.outForDeliveryForm.dirty);
  }

  radioChange(newValue: Options) {
    this.outForDeliveryForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof OutForDeliveryComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.outForDeliveryForm.value;
          const locationCode = user?.location;
          const date = this.commonUtils.getFormattedDate(formValue.date);

          const request = {
            locationCode,
            date,
          };
          return this.codsReportsService
            .getCODOutForDeliveryReport(request)
            .pipe(
              take(1),
              map((data) => {
                const formatType =
                  this.outForDeliveryForm.get('reportFormat')?.value;
                const CODOutForDelivery = data.response.CODOutForDelivery;
                if (CODOutForDelivery?.length) {
                  if (formatType === Options.CSV) {
                    const customHeader = [
                      ['', '', '', 'COD OUT FOR DELIVERY REPORT', '', ''],
                      [
                        '',
                        'LOCATION:',
                        '',
                        `${data?.response.location}`,
                        '',
                        `${data?.response.date}`,
                      ],
                    ];

                    const formatedDate = formatDate(
                      new Date(),
                      'MM-dd-yyyy',
                      'en-US',
                    );
                    const customFooter = [['Printed:', `${formatedDate}`]];
                    this.reportUtils.exportCSVReport(
                      CODOutForDelivery,
                      FILE_NAME,
                      customHeader,
                      customFooter,
                    );
                  }
                  if (formatType === Options.PDF) {
                    this.codOutForDeliveryReport = data;
                    setTimeout(() => {
                      const content =
                        this.pdfContainer.element.nativeElement.innerHTML;
                      this.reportUtils.exportPdfReport(
                        content,
                        PDF_REPORT.NAME,
                        FILE_NAME,
                      );
                    }, 0);
                  }
                } else {
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.NO_DATA_AVAILABLE,
                  );
                }

                this.dialogRef.close();
              }),
            );
        }),
      )
      .subscribe();
  }
}
